<template>
    <!-- MY1-3080: "Add from My Spending" — pick a transaction on the benefit's
         card to record as usage. Lists every expense on the card (not just the
         benefit's category: the whole point is correcting a mis-categorised
         match), 50 at a time with infinite scroll; search, period and category
         filter server-side. -->
    <UiModal
        :is-open="isOpen"
        width="720px"
        :is-teleported="true"
        @close="close"
    >
        <template #title>
            <div class="flex flex-col gap-1 pr-10">
                <h5 class="ui-modal-title">Select transaction to add usage</h5>
                <p
                    class="text-sm text-slate-500 dark:text-slate-400"
                    data-test="picker-subtitle"
                >
                    {{ benefitTitle }}
                    <template v-if="cardLabel">· {{ cardLabel }}</template>
                </p>
            </div>
        </template>

        <template #content>
            <div class="flex flex-col gap-4">
                <!-- Filters: search, period, category -->
                <div class="flex flex-col gap-2 sm:flex-row sm:items-center">
                    <div class="relative flex-1 min-w-0">
                        <div
                            class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3"
                        >
                            <Icon
                                icon="search"
                                class="h-4 w-4 text-slate-500 dark:text-slate-400"
                            />
                        </div>
                        <input
                            v-model="search"
                            type="search"
                            placeholder="Search transaction"
                            aria-label="Search transaction"
                            class="block h-10 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-sm text-slate-800 placeholder-slate-400 focus:border-slate-800 focus:outline-none focus:ring-1 focus:ring-slate-800 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:placeholder-slate-500"
                            data-test="picker-search"
                        />
                    </div>
                    <PeriodControl v-model="period" default-preset="all" />
                    <ButtonPopover
                        buttonClass="btn-base btn-white !pl-4 !pr-3 h-10"
                        placement="bottom-end"
                        :teleport="true"
                    >
                        <template #button-content>
                            <span
                                class="text-sm font-medium text-slate-800 dark:text-slate-100"
                            >
                                {{ category || 'Category' }}
                            </span>
                            <Icon
                                icon="chevron-down-small"
                                class="h-5 w-5 text-slate-500"
                            />
                        </template>
                        <template #default="{ closePopover }">
                            <ul
                                class="min-w-[220px] p-1"
                                data-test="picker-categories"
                            >
                                <li
                                    v-for="option in categoryOptions"
                                    :key="option"
                                >
                                    <button
                                        type="button"
                                        class="flex w-full items-center gap-2 rounded-md p-2 text-left text-sm text-slate-800 hover:bg-slate-100 focus:bg-slate-100 focus:outline-none dark:text-slate-100 dark:hover:bg-slate-700"
                                        :class="{
                                            'bg-slate-100 dark:bg-slate-700':
                                                category === option,
                                        }"
                                        :data-test="`picker-category-${option}`"
                                        @click="
                                            selectCategory(option);
                                            closePopover();
                                        "
                                    >
                                        <Icon
                                            :icon="iconForCategory(option)"
                                            class="h-4 w-4 shrink-0 text-slate-500"
                                        />
                                        <span class="truncate">
                                            {{ option }}
                                        </span>
                                    </button>
                                </li>
                                <li v-if="category">
                                    <button
                                        type="button"
                                        class="flex w-full items-center gap-2 rounded-md p-2 text-left text-sm text-slate-600 hover:bg-slate-100 focus:outline-none dark:text-slate-300 dark:hover:bg-slate-700"
                                        data-test="picker-category-reset"
                                        @click="
                                            selectCategory(null);
                                            closePopover();
                                        "
                                    >
                                        Reset
                                    </button>
                                </li>
                            </ul>
                        </template>
                    </ButtonPopover>
                </div>

                <!-- Transactions, grouped by day, infinite scroll -->
                <div
                    ref="listRef"
                    class="-mx-2 max-h-[45vh] min-h-[200px] overflow-y-auto px-2"
                    data-test="picker-list"
                    @scroll.passive="onScroll"
                >
                    <template v-if="groups.length">
                        <section
                            v-for="group in groups"
                            :key="group.key"
                            :data-test="`picker-group-${group.key}`"
                        >
                            <h6
                                class="py-2 text-sm font-medium text-slate-500 dark:text-slate-400"
                            >
                                {{ group.label }}
                            </h6>
                            <label
                                v-for="transaction in group.items"
                                :key="transaction.id"
                                v-tooltip="
                                    isLinked(transaction)
                                        ? linkedTooltip(transaction)
                                        : null
                                "
                                class="flex items-center gap-3 border-b border-slate-100 py-3 dark:border-slate-700"
                                :class="
                                    isLinked(transaction)
                                        ? 'cursor-not-allowed opacity-50'
                                        : 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/40'
                                "
                                :data-test="`picker-row-${transaction.id}`"
                                :data-linked="
                                    isLinked(transaction) ? 'true' : 'false'
                                "
                            >
                                <span
                                    class="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700"
                                >
                                    <img
                                        v-if="transaction.merchant_logo_url"
                                        :src="transaction.merchant_logo_url"
                                        :alt="transaction.merchant_name"
                                        class="h-10 w-10 object-cover"
                                    />
                                    <Icon
                                        v-else
                                        :icon="
                                            iconForCategory(
                                                transaction.merchant_category
                                            )
                                        "
                                        class="h-4 w-4 text-slate-500"
                                    />
                                </span>
                                <span class="min-w-0 flex-1">
                                    <span
                                        class="block truncate text-sm font-medium text-slate-800 dark:text-slate-100"
                                    >
                                        {{
                                            transaction.merchant_name ||
                                            'Transaction'
                                        }}
                                    </span>
                                    <span
                                        class="block truncate text-sm text-slate-500 dark:text-slate-400"
                                    >
                                        {{
                                            transaction.merchant_category ||
                                            'Other'
                                        }}
                                    </span>
                                </span>
                                <span
                                    class="shrink-0 text-sm font-medium text-slate-800 dark:text-slate-100"
                                >
                                    {{
                                        filters.formatSmartMoney(
                                            Math.abs(
                                                Number(transaction.amount) || 0
                                            )
                                        )
                                    }}
                                </span>
                                <input
                                    type="radio"
                                    name="benefit-transaction-picker"
                                    class="h-5 w-5 shrink-0 border-slate-300 text-10x-orange focus:ring-10x-orange"
                                    :value="transaction.id"
                                    :checked="selected?.id === transaction.id"
                                    :disabled="isLinked(transaction)"
                                    @change="select(transaction)"
                                />
                            </label>
                        </section>
                        <p
                            v-if="loadingMore"
                            class="py-3 text-center text-sm text-slate-500 dark:text-slate-400"
                            data-test="picker-loading-more"
                        >
                            Loading more…
                        </p>
                    </template>

                    <div
                        v-else-if="loading"
                        class="flex flex-col gap-3 py-2"
                        data-test="picker-loading"
                        aria-busy="true"
                    >
                        <div
                            v-for="n in 5"
                            :key="n"
                            class="flex items-center gap-3 py-2"
                        >
                            <span
                                class="h-10 w-10 animate-pulse rounded-full bg-slate-100 dark:bg-slate-700"
                            />
                            <span class="flex-1 space-y-2">
                                <span
                                    class="block h-3 w-1/2 animate-pulse rounded bg-slate-100 dark:bg-slate-700"
                                />
                                <span
                                    class="block h-3 w-1/3 animate-pulse rounded bg-slate-100 dark:bg-slate-700"
                                />
                            </span>
                            <span
                                class="h-3 w-12 animate-pulse rounded bg-slate-100 dark:bg-slate-700"
                            />
                        </div>
                    </div>

                    <div
                        v-else-if="error"
                        class="py-10 text-center text-sm text-slate-500 dark:text-slate-400"
                        data-test="picker-error"
                    >
                        We couldn't load your transactions.
                        <button
                            type="button"
                            class="ml-1 font-medium text-slate-800 underline dark:text-slate-100"
                            @click="reload"
                        >
                            Try again
                        </button>
                    </div>

                    <p
                        v-else
                        class="py-10 text-center text-sm text-slate-500 dark:text-slate-400"
                        data-test="picker-empty"
                    >
                        No results found
                    </p>
                </div>

                <!-- Selected: amount and date replace the manual form's fields -->
                <div
                    v-if="selected"
                    class="grid grid-cols-1 gap-3 border-t border-slate-200 pt-4 sm:grid-cols-2 dark:border-slate-700"
                    data-test="picker-fill"
                >
                    <div>
                        <label
                            for="picker-amount"
                            class="mb-1 block text-sm text-slate-800 dark:text-slate-100"
                        >
                            Redeemed
                        </label>
                        <div class="relative">
                            <span
                                class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-sm text-slate-500"
                            >
                                $
                            </span>
                            <input
                                id="picker-amount"
                                v-model="amount"
                                type="number"
                                inputmode="decimal"
                                min="0"
                                step="0.01"
                                class="block h-10 w-full rounded-lg border bg-white pl-7 pr-3 text-sm text-slate-800 focus:outline-none focus:ring-1 dark:bg-slate-800 dark:text-slate-100"
                                :class="
                                    amountError
                                        ? 'border-red-400 focus:border-red-500 focus:ring-red-500'
                                        : 'border-slate-200 focus:border-slate-800 focus:ring-slate-800 dark:border-slate-600'
                                "
                                data-test="picker-amount"
                            />
                        </div>
                        <p
                            v-if="amountError"
                            class="mt-1 text-sm text-red-600"
                            data-test="picker-amount-error"
                        >
                            {{ amountError }}
                        </p>
                    </div>
                    <div>
                        <label
                            for="picker-date"
                            class="mb-1 block text-sm text-slate-800 dark:text-slate-100"
                        >
                            Date
                        </label>
                        <input
                            id="picker-date"
                            type="text"
                            readonly
                            :value="selectedDateLabel"
                            class="block h-10 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm text-slate-600 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-300"
                            data-test="picker-date"
                        />
                    </div>
                </div>
            </div>
        </template>

        <template #footer>
            <div class="flex items-center justify-between gap-3">
                <span
                    class="text-sm text-slate-500 dark:text-slate-400"
                    data-test="picker-hint"
                >
                    <template v-if="selected">
                        {{
                            filters.formatSmartMoney(remainingAmount)
                        }}
                        available
                    </template>
                    <template v-else>Select transaction first</template>
                </span>
                <div class="flex items-center gap-3">
                    <button
                        type="button"
                        class="btn-base btn-white"
                        @click="close"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        class="btn-base btn-dark"
                        :disabled="!canSubmit"
                        data-test="picker-submit"
                        @click="submit"
                    >
                        Add usage
                    </button>
                </div>
            </div>
        </template>
    </UiModal>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import dayjs from 'dayjs';
import UiModal from '@/components/ui/modal/ui-modal.vue';
import ButtonPopover from '@/components/ui/button-popover.vue';
import PeriodControl from '@/components/ui/period-control/period-control.vue';
import Icon from '@/components/ui/icon/icon.vue';
import filters from '@/filters';
import { Apii } from '@/services/api.js';
import { iconForCategory } from '@/utils/category-icons';
import { SPENDING_EXPENSE_CATEGORIES } from '@/utils/spending-categories';
import { useCardBenefitsStore } from '@/store/card-benefits';

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

const props = defineProps({
    isOpen: {
        type: Boolean,
        default: false,
    },
    // The benefit row from the benefits store (flat / by-card item).
    benefit: {
        type: Object,
        default: null,
    },
    // Value left in the current period — the cap on what may be recorded.
    remainingAmount: {
        type: [Number, String],
        default: 0,
    },
    categories: {
        type: Array,
        default: () => [...SPENDING_EXPENSE_CATEGORIES],
    },
});

const emit = defineEmits(['close', 'linked']);

const store = useCardBenefitsStore();
const api = new Apii();

const search = ref('');
const period = ref({
    preset: 'all',
    start: null,
    end: null,
    label: 'All time',
});
const category = ref(null);

const transactions = ref([]);
const page = ref(1);
const lastPage = ref(1);
const loading = ref(false);
const loadingMore = ref(false);
const error = ref(false);
const selected = ref(null);
const amount = ref('');
const submitting = ref(false);
const listRef = ref(null);

let fetchSequence = 0;
let searchTimer = null;

const benefitTitle = computed(
    () => props.benefit?.benefit?.benefit_title || 'Benefit'
);

const cardLabel = computed(() => {
    const title = props.benefit?.card?.title;
    const lastFour = props.benefit?.connected_account_last_four;

    if (!title) {
        return '';
    }

    return lastFour ? `${title} (${lastFour})` : title;
});

const accountGuid = computed(() => props.benefit?.account?.guid || null);

const remainingAmount = computed(() =>
    Math.max(0, Number(props.remainingAmount) || 0)
);

// Payments, refunds and income are never benefit usage.
const isExpense = (transaction) =>
    transaction.is_expense !== false && transaction.type !== 'CREDIT';

const categoryOptions = computed(() => {
    const seen = new Set(props.categories);
    const extra = transactions.value
        .map((t) => t.merchant_category)
        .filter((c) => c && !seen.has(c));

    return [...props.categories, ...new Set(extra)];
});

/**
 * Today / Yesterday / "Apr 27" (this year) / "Apr 27, 2025".
 */
function groupLabel(dateString) {
    const date = dayjs(dateString);

    if (!date.isValid()) {
        return 'Unknown date';
    }

    const today = dayjs().startOf('day');

    if (date.isSame(today, 'day')) {
        return 'Today';
    }

    if (date.isSame(today.subtract(1, 'day'), 'day')) {
        return 'Yesterday';
    }

    return date.format(date.year() === today.year() ? 'MMM D' : 'MMM D, YYYY');
}

const groups = computed(() => {
    const byDay = new Map();

    for (const transaction of transactions.value) {
        if (!isExpense(transaction)) {
            continue;
        }

        const key = transaction.transaction_date || 'unknown';

        if (!byDay.has(key)) {
            byDay.set(key, { key, label: groupLabel(key), items: [] });
        }

        byDay.get(key).items.push(transaction);
    }

    return [...byDay.values()];
});

const isLinked = (transaction) =>
    Array.isArray(transaction.linked_benefits) &&
    transaction.linked_benefits.length > 0;

const linkedTooltip = (transaction) =>
    `This transaction already linked to ${
        transaction.linked_benefits?.[0]?.benefit_title || 'a'
    } benefit usage record`;

const selectedDateLabel = computed(() =>
    selected.value?.transaction_date
        ? dayjs(selected.value.transaction_date).format('MMM D, YYYY')
        : ''
);

const parsedAmount = computed(() => {
    const value = parseFloat(amount.value);

    return Number.isFinite(value) ? Math.round(value * 100) / 100 : NaN;
});

// The design's error list for this modal.
const amountError = computed(() => {
    if (!selected.value) {
        return null;
    }

    if (amount.value === '' || amount.value === null) {
        return 'Enter redeemed amount';
    }

    if (!Number.isFinite(parsedAmount.value) || parsedAmount.value <= 0) {
        return 'Enter redeemed amount';
    }

    if (parsedAmount.value > remainingAmount.value) {
        return `Enter an amount no more than the available ${filters.formatSmartMoney(remainingAmount.value)}`;
    }

    return null;
});

const canSubmit = computed(
    () => !!selected.value && !amountError.value && !submitting.value
);

function requestParams(pageNumber) {
    const params = {
        page: pageNumber,
        per_page: PAGE_SIZE,
        sort: 'date',
        sort_dir: 'desc',
    };

    if (accountGuid.value) {
        params.account_guid = accountGuid.value;
    }

    if (period.value?.start) {
        params.start_date = period.value.start;
    }

    if (period.value?.end) {
        params.end_date = period.value.end;
    }

    const term = search.value.trim();

    if (term) {
        params.search = term;
    }

    if (category.value) {
        params.category = category.value;
    }

    return params;
}

async function fetchPage(pageNumber, { append = false } = {}) {
    const requestId = ++fetchSequence;

    if (append) {
        loadingMore.value = true;
    } else {
        loading.value = true;
        error.value = false;
    }

    try {
        const response = await api.apiCall(
            '/transactions',
            'get',
            requestParams(pageNumber)
        );

        if (requestId !== fetchSequence) {
            return;
        }

        const payload = response.data || {};
        const rows = Array.isArray(payload.data) ? payload.data : [];

        transactions.value = append ? [...transactions.value, ...rows] : rows;
        page.value = payload.meta?.current_page ?? pageNumber;
        lastPage.value = payload.meta?.last_page ?? pageNumber;
    } catch {
        if (requestId === fetchSequence) {
            error.value = true;

            if (!append) {
                transactions.value = [];
            }
        }
    } finally {
        if (requestId === fetchSequence) {
            loading.value = false;
            loadingMore.value = false;
        }
    }
}

function reload() {
    selected.value = null;
    amount.value = '';
    fetchPage(1);
}

async function loadMore() {
    if (loading.value || loadingMore.value || page.value >= lastPage.value) {
        return;
    }

    await fetchPage(page.value + 1, { append: true });
}

function onScroll(event) {
    const el = event.target;

    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 80) {
        loadMore();
    }
}

function select(transaction) {
    if (isLinked(transaction)) {
        return;
    }

    selected.value = transaction;

    // Pre-fill with the purchase, capped at what the period has left —
    // issuers apply statement credits the same way.
    const purchase = Math.abs(Number(transaction.amount) || 0);
    const prefill = Math.min(purchase, remainingAmount.value);

    amount.value = prefill > 0 ? prefill.toFixed(2) : '';
}

function selectCategory(option) {
    category.value = option;
}

function resetState() {
    search.value = '';
    period.value = { preset: 'all', start: null, end: null, label: 'All time' };
    category.value = null;
    transactions.value = [];
    page.value = 1;
    lastPage.value = 1;
    selected.value = null;
    amount.value = '';
    error.value = false;
}

function close() {
    emit('close');
}

async function submit() {
    if (!canSubmit.value) {
        return;
    }

    submitting.value = true;

    try {
        const failure = await store.linkTransactionToBenefit(
            props.benefit,
            selected.value,
            parsedAmount.value
        );

        if (failure === null) {
            emit('linked', {
                transaction: selected.value,
                amount: parsedAmount.value,
            });
            close();
        }
    } finally {
        submitting.value = false;
    }
}

watch(
    () => props.isOpen,
    (open) => {
        if (open) {
            resetState();
            fetchPage(1);
        }
    },
    { immediate: true }
);

watch(search, () => {
    clearTimeout(searchTimer);
    searchTimer = setTimeout(() => reload(), SEARCH_DEBOUNCE_MS);
});

watch([period, category], () => reload(), { deep: true });

defineExpose({ loadMore, reload });
</script>
